/**
 * Where a new page starts, and what has focus when it gets there.
 *
 * A single-page app changes the URL without loading a document, so nothing resets the
 * scroll position: following a link from halfway down an article to the listing left the
 * visitor halfway down the listing, looking at whatever happened to be at that height.
 * Every link on the site did this.
 *
 * Three rules, and the second and third are why this is not one `scrollTo`.
 *
 * **Back and Forward go back to where the visitor was**, which the browser cannot do for
 * us here. It restores the scroll position as soon as the history entry changes, and at
 * that moment the new page has not been fetched: the document is one screen tall, the
 * restore clamps to the top, and Back always lands at the top of a page the visitor was
 * halfway down. So the position is remembered per history entry and restored once the
 * content has actually arrived. `useNavigationType` reports `POP` for both directions,
 * which is the only reliable way to tell "the visitor went back" from "the visitor
 * followed a link".
 *
 * **The query string is not a new page.** The article listing keeps its filter and its
 * "see more" count in the URL, so `?page=2` and `?category=loty` arrive here as location
 * changes - and jumping to the top when somebody presses "see more" would throw away the
 * place they were reading. Only `pathname` and `hash` are watched.
 *
 * **A hash waits for the content.** `#zespol` points at a section that does not exist
 * until the CMS has answered, so scrolling immediately would find nothing and do nothing.
 * `onHeadApplied` fires when the page has actually arrived - see lib/head.ts - which is
 * the same signal the analytics page view waits for.
 *
 * Focus moves to `<main>` on the same events. Scrolling is invisible to a screen reader:
 * without this, following a link leaves focus on a link that is no longer on the page and
 * the next Tab continues from wherever that was. `preventScroll` because the scrolling is
 * decided above, and `main` sits below a sticky header that focus would otherwise scroll
 * past.
 */
import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { onHeadApplied } from './head';

/**
 * `instant`, not the default.
 *
 * `html { scroll-behavior: smooth }` is set globally for in-page anchors, and it applies
 * to this too - so without asking for `instant` a page change animates a long scroll up
 * through content the visitor has already left.
 */
const scrollTo = (top: number) => window.scrollTo({ top, left: 0, behavior: 'instant' });

/**
 * Where the visitor was on each history entry, so Back can put them back.
 *
 * Module-level and keyed by `location.key`, which React Router makes unique per entry.
 * Capped because a long session in one tab would otherwise keep every position it has ever
 * seen; fifty is far more history than anybody walks back through, and losing the oldest
 * means an unusually deep Back lands at the top rather than misbehaving.
 */
const positions = new Map<string, number>();
const LIMIT = 50;

function remember(key: string, top: number) {
  positions.delete(key);
  positions.set(key, top);
  if (positions.size > LIMIT) positions.delete(positions.keys().next().value as string);
}

export function useScrollReset() {
  const { pathname, hash, key } = useLocation();
  const navigationType = useNavigationType();

  // Keep the current entry's position up to date. A passive listener, so it never delays a
  // scroll, and it also runs on the way out - the last thing that happens on this entry is
  // the visitor leaving it from wherever they had got to.
  useEffect(() => {
    const save = () => remember(key, window.scrollY);
    window.addEventListener('scroll', save, { passive: true });
    return () => {
      save();
      window.removeEventListener('scroll', save);
    };
  }, [key]);

  useEffect(() => {
    if (navigationType === 'POP') {
      const saved = positions.get(key);
      if (saved === undefined) return undefined;

      // Twice, and both are needed: once now in case the page is already rendered, and
      // once when the content arrives, because until it does the document is too short to
      // scroll that far and the browser silently clamps to the bottom of what exists.
      scrollTo(saved);
      return onHeadApplied(() => scrollTo(saved));
    }

    document.getElementById('main')?.focus({ preventScroll: true });

    if (!hash) {
      scrollTo(0);
      return undefined;
    }

    // Until the section exists there is nothing to scroll to, so start at the top and move
    // once the page is really here.
    scrollTo(0);
    return onHeadApplied(() => {
      document.getElementById(hash.slice(1))?.scrollIntoView();
    });
  }, [pathname, hash, key, navigationType]);
}

/** Mounted inside the router; renders nothing. */
export default function ScrollReset() {
  useScrollReset();
  return null;
}
